"use client";

import Reveal from "@/app/components/Reveal";
import Link from "next/link";
import { useLocale } from "@/app/components/LocaleProvider";
import { getPathWithLocale } from "@/lib/i18n";
import { MailIcon, PhoneIcon, TelegramIcon, WhatsAppIcon, ContactsIcon, ChatIcon } from "./ContactIcons";
import { CONTACTS } from "@/lib/site-contacts";
import { IntegrationCard, BenefitCard } from "./IntegrationCards";

type AiCard = { abbr: string; title: string; desc: string };

const aiCopy: Record<string, { title: string; intro: string; cards: AiCard[]; benefits: { number: string; title: string; text: string }[] }> = {
    ru: {
        title: "Интеграция искусственного интеллекта в проекты",
        intro: "Искусственный интеллект перестал быть футуристической концепцией — это рабочий инструмент, который уже сегодня увеличивает прибыль, ускоряет разработку и улучшает пользовательский опыт. Мы внедряем AI в продукты любого масштаба: от стартапов до корпоративных экосистем.",
        cards: [
            { abbr: "VUI / CA", title: "Чат-боты и голосовые ассистенты", desc: "Умные консультанты, поддержка 24/7, обработка заказов в мессенджерах и на сайте." },
            { abbr: "CV", title: "Компьютерное зрение", desc: "Распознавание лиц, анализ изображений, контроль качества, сегментация объектов, безопасность." },
            { abbr: "IG / TTI", title: "Генерация фото", desc: "Создание уникальных изображений, рекламных креативов, аватар, обработка и реставрация фото." },
            { abbr: "VG / TTV", title: "Генерация видео", desc: "Создание видеороликов по тексту, нейросетевые фильтры, анимация, глубокие фейки (синтез личности)." },
            { abbr: "TTS / ASR", title: "Генерация аудио и синтез речи", desc: "Озвучка текста (TTS), клонирование голоса, создание подкастов, музыкальных треков и звуковых эффектов." },
            { abbr: "BI / DA", title: "Анализ данных и бизнес-аналитика", desc: "Обработка больших данных, визуализация, поиск инсайтов, построение дашбордов, ETL-процессы." },
            { abbr: "NLP", title: "Обработка естественного языка (NLP)", desc: "Анализ тональности, суммаризация текстов, машинный перевод, извлечение сущностей, классификация документов." },
            { abbr: "PA", title: "Прогнозная аналитика (предиктивные модели)", desc: "Предсказание оттока клиентов, спроса, поломок оборудования, кредитных рисков, цен на сырье." },
            { abbr: "RPA", title: "Роботизированная автоматизация (RPA + AI)", desc: "Умные роботы, автоматизирующие рутинные офисные задачи: заполнение форм, перенос данных между CRM/ERP." },
            { abbr: "AI Security / AD", title: "AI-безопасность (детекция аномалий)", desc: "Обнаружение кибератак, мошеннических транзакций, подозрительного поведения пользователей в реальном времени." },
            { abbr: "GD", title: "Генеративный дизайн и инженерия", desc: "Создание чертежей, архитектурных планов, дизайна интерфейсов, 3D-моделей по текстовому описанию." },
            { abbr: "RS / RecSys", title: "Рекомендательные системы", desc: "Персональные рекомендации товаров, контента, музыки, фильмов, образовательных материалов (как у Netflix, Amazon, Spotify)." },
        ],
        benefits: [
            { number: "01", title: "Рост эффективности", text: "Автоматизируем до 80% рутинных задач, высвобождая ресурсы команды для творчества и стратегии." },
            { number: "02", title: "Точность и прогнозы", text: "Машинное обучение анализирует терабайты данных и предсказывает поведение пользователей, тренды и сбои еще до их появления." },
            { number: "03", title: "Легкая интеграция", text: "Встраиваем AI в ваш текущий стек (Web, Mobile, Desktop, IoT, Cloud) без переписывания кода. Гибкие API и SDK." },
            { number: "04", title: "Безопасность и контроль", text: "Работаем с вашими данными локально (on-premise) или в защищенном облаке. Соответствуем 152-ФЗ и GDPR." },
        ],
    },
    en: {
        title: "AI Integration into Your Projects",
        intro: "Artificial intelligence is no longer a futuristic concept — it's a working tool that already today increases profits, accelerates development, and improves user experience. We integrate AI into products of any scale: from startups to enterprise ecosystems.",
        cards: [
            { abbr: "VUI / CA", title: "Chatbots and Voice Assistants", desc: "Smart consultants, 24/7 support, order processing in messengers and on the website." },
            { abbr: "CV", title: "Computer Vision", desc: "Face recognition, image analysis, quality control, object segmentation, security." },
            { abbr: "IG / TTI", title: "Photo Generation", desc: "Creating unique images, ad creatives, avatars, photo editing and restoration." },
            { abbr: "VG / TTV", title: "Video Generation", desc: "Creating videos from text, neural filters, animation, deepfakes (identity synthesis)." },
            { abbr: "TTS / ASR", title: "Audio Generation and Speech Synthesis", desc: "Text-to-speech (TTS), voice cloning, podcasts, music tracks, and sound effects." },
            { abbr: "BI / DA", title: "Data Analysis and Business Analytics", desc: "Big data processing, visualization, insight discovery, dashboards, ETL processes." },
            { abbr: "NLP", title: "Natural Language Processing (NLP)", desc: "Sentiment analysis, text summarization, machine translation, entity extraction, document classification." },
            { abbr: "PA", title: "Predictive Analytics (Predictive Models)", desc: "Predicting customer churn, demand, equipment failures, credit risks, commodity prices." },
            { abbr: "RPA", title: "Robotic Process Automation (RPA + AI)", desc: "Smart robots automating routine office tasks: form filling, data transfer between CRM/ERP." },
            { abbr: "AI Security / AD", title: "AI Security (Anomaly Detection)", desc: "Detecting cyberattacks, fraudulent transactions, and suspicious user behavior in real time." },
            { abbr: "GD", title: "Generative Design and Engineering", desc: "Creating blueprints, architectural plans, UI design, 3D models from text descriptions." },
            { abbr: "RS / RecSys", title: "Recommendation Systems", desc: "Personalized recommendations of products, content, music, movies, and educational materials (like Netflix, Amazon, Spotify)." },
        ],
        benefits: [
            { number: "01", title: "Efficiency Growth", text: "Automate up to 80% of routine tasks, freeing team resources for creativity and strategy." },
            { number: "02", title: "Accuracy and Predictions", text: "Machine learning analyzes terabytes of data and predicts user behavior, trends, and failures before they occur." },
            { number: "03", title: "Easy Integration", text: "We embed AI into your current stack (Web, Mobile, Desktop, IoT, Cloud) without rewriting code. Flexible APIs and SDKs." },
            { number: "04", title: "Security and Control", text: "We work with your data locally (on-premise) or in a secure cloud. Compliant with 152-FZ and GDPR." },
        ],
    },
};

export default function AiIntegrationBlock({ locale }: { locale: string }) {
    const copy = locale === "ru" ? aiCopy.ru : aiCopy.en;
    const localeFromHook = useLocale();

    return (
        <section className="py-10 md:py-14 max-md:py-9">
            <div className="lg:flex lg:items-start">

                {/* Фото — слева, липкое: залипает на экране, пока текст скроллится рядом */}
                <Reveal
                    className="mt-12 lg:mt-0 lg:sticky lg:top-[80px] lg:self-start lg:shrink-0 lg:w-[448px] max-md:hidden"
                    scale={0.95}
                    duration={0.8}
                >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src="/images/ai_business-448.webp"
                        srcSet="/images/ai_business-448.webp 448w, /images/ai_business-714.webp 714w, /images/ai_business.webp 1173w"
                        sizes="(max-width: 768px) 100vw, 448px"
                        alt={copy.title}
                        loading="lazy"
                        width="448"
                        height="250"
                        className="w-full border border-white/10 object-cover aspect-[3/4] lg:-translate-x-[calc(10vw_+_25px)]"
                    />
                </Reveal>

                {/* Контент — справа от фото */}
                <div className="relative z-10 mt-10 px-4 lg:mt-0 lg:flex-1 lg:flex lg:justify-center lg:px-8 max-md:mt-0">
                    <div className="w-full max-w-3xl lg:-ml-36">
                        <h2 className="text-3xl font-bold tracking-tight text-white/95 max-md:text-2xl">
                            {copy.title}
                        </h2>
                        <p className="mt-3 max-w-3xl text-sm text-white/70 text-left max-md:mt-2">
                            {copy.intro}
                        </p>

                        {/* 12 карточек */}
                        <div className="mt-8 grid auto-rows-fr grid-cols-2 gap-2.5 sm:grid-cols-2 max-md:mt-6 sm:gap-3 md:gap-4">
                            {copy.cards.map((card) => (
                                <IntegrationCard key={card.abbr} {...card} />
                            ))}
                        </div>

                        {/* 4 преимущества */}
                        <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 max-md:mt-6 max-md:gap-2.5">
                            {copy.benefits.map((benefit) => (
                                <BenefitCard key={benefit.number} {...benefit} />
                            ))}
                        </div>

                        {/* Контакты */}
                        <div className="mt-8 flex flex-wrap gap-3 max-md:mt-6">
                            <Link
                                href={getPathWithLocale("/contacts", localeFromHook)}
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200"
                                title="Контакты"
                            >
                                <ContactsIcon />
                            </Link>
                            <a
                                href={`mailto:${CONTACTS.email}`}
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200"
                                title={CONTACTS.email}
                            >
                                <MailIcon />
                            </a>
                            <a
                                href={`tel:${CONTACTS.phone}`}
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200"
                                title={CONTACTS.phoneDisplay}
                            >
                                <PhoneIcon />
                            </a>
                            <a
                                href={CONTACTS.whatsappUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200"
                                title={`WhatsApp ${CONTACTS.whatsappDisplay}`}
                            >
                                <WhatsAppIcon />
                            </a>
                            <a
                                href={CONTACTS.telegramUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200"
                                title={CONTACTS.telegramHandle}
                            >
                                <TelegramIcon />
                            </a>
                            <Link
                                href={getPathWithLocale("/chat", localeFromHook)}
                                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 hover:bg-white/5 transition-all duration-200"
                                title={localeFromHook.startsWith("en") ? "Discuss in chat" : "Обсудить в чате"}
                            >
                                <ChatIcon />
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}